import { useRef, useState, type DragEvent } from 'react';
import {
  confirmEnteredFigure, findConflicts, resolveConflict, setCandidateState, syncFinancialsFromDocuments,
} from '../../documents/reconcile.js';
import { describeSource } from '../../core/financials.js';
import { fmtMoney, parseAmount } from '../../intake/parseAnswer.js';
import {
  DOC_KIND_LABELS, FINANCIAL_FIELD_LABELS, fact,
  type Candidate, type DocKind, type DocumentRecord, type FinancialField,
} from '../../workspace/schema.js';
import { deleteDocBlob, getDocBlob } from '../../workspace/store.js';
import { ACCEPT, ingest, openSourcePage } from '../ingest.js';
import { PageHead } from '../components.js';
import { useWorkspace } from '../state.js';

const KIND_ORDER: DocKind[] = [
  'pl', 'tax-return', 'balance-sheet', 'bank-statement', 'business-plan', 'debt-schedule',
  'seller-financials', 'purchase-agreement', 'personal-financial-statement', 'other',
];

function Uploader() {
  const { update } = useWorkspace();
  const [kind, setKind] = useState<DocKind>('pl');
  const [busy, setBusy] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const handle = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      setBusy(`Reading ${file.name}…`);
      const rec = await ingest(file, kind);
      update((w) => ({ ...w, documents: [...w.documents, rec] }));
    }
    setBusy(null);
    if (input.current) input.current.value = '';
  };

  const onDrop = (e: DragEvent) => { e.preventDefault(); setOver(false); void handle(e.dataTransfer.files); };

  return (
    <section className="card">
      <h2>Add a document</h2>
      <div className="grid-2" style={{ alignItems: 'end' }}>
        <div className="field">
          <label htmlFor="doc-kind">What is it?</label>
          <select id="doc-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as DocKind)}>
            {KIND_ORDER.map((k) => <option key={k} value={k}>{DOC_KIND_LABELS[k]}</option>)}
          </select>
        </div>
        <p className="small muted" style={{ margin: 0 }}>PDF, Excel or CSV. Read on this device — files are never uploaded to a server.</p>
      </div>
      <div
        className={`dropzone ${over ? 'over' : ''}`}
        style={{ marginTop: 14 }}
        role="button"
        tabIndex={0}
        onClick={() => input.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        aria-describedby="drop-help"
      >
        <strong>{busy ?? 'Drop files here or click to choose'}</strong>
        <span id="drop-help" className="small muted">Figures found are shown with the page they came from, for you to confirm.</span>
      </div>
      <input ref={input} type="file" accept={ACCEPT} multiple hidden onChange={(e) => void handle(e.target.files)} />
    </section>
  );
}

function CandidateRow({ doc, c }: { doc: DocumentRecord; c: Candidate }) {
  const { update } = useWorkspace();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const where = c.page !== undefined ? `page ${c.page}` : c.sheet ? `sheet "${c.sheet}"` : '';
  const set = (state: Candidate['state'], value?: number) => update((w) => setCandidateState(w, doc.id, c.id, state, value));

  return (
    <div className={`cand ${c.state}`}>
      <div>
        <div className="what">{FINANCIAL_FIELD_LABELS[c.field]}{c.period && <span className="badge" style={{ marginLeft: 8 }}>{c.period}</span>}</div>
        <div className="small faint">Matched &ldquo;{c.label}&rdquo;{where && ` on ${where}`}</div>
        <div className="snip">{c.snippet}</div>
      </div>
      <div className="val">{fmtMoney(c.value)}{c.state === 'confirmed' && <div className="small" style={{ fontWeight: 600 }}>Confirmed</div>}</div>
      <div className="acts">
        {editing ? (
          <form className="row" onSubmit={(e) => {
            e.preventDefault();
            const p = parseAmount(val);
            if (p) { set('confirmed', p.value); setEditing(false); }
          }}>
            <input className="input" style={{ width: 160 }} autoFocus aria-label="Corrected amount" value={val} onChange={(e) => setVal(e.target.value)} placeholder={String(c.value)} />
            <button className="btn small primary" type="submit">Save</button>
            <button className="btn small ghost" type="button" onClick={() => setEditing(false)}>Cancel</button>
          </form>
        ) : (
          <>
            {c.state !== 'confirmed' && <button className="btn small primary" type="button" onClick={() => set('confirmed')}>Confirm</button>}
            <button className="btn small" type="button" onClick={() => { setVal(String(c.value)); setEditing(true); }}>Correct</button>
            {c.state !== 'rejected' && <button className="btn small ghost" type="button" onClick={() => set('rejected')}>Not this</button>}
            {c.state === 'rejected' && <button className="btn small ghost" type="button" onClick={() => set('pending')}>Undo</button>}
            {c.page !== undefined && (
              <button className="link-btn small" type="button" onClick={() => void openSourcePage(getDocBlob, doc.id, c.page)}>
                View page {c.page}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DocCard({ doc }: { doc: DocumentRecord }) {
  const { update } = useWorkspace();
  const [showDone, setShowDone] = useState(false);
  const pending = doc.candidates.filter((c) => c.state === 'pending');
  const done = doc.candidates.filter((c) => c.state !== 'pending');
  const statusBadge = {
    reading: <span className="badge">Reading…</span>,
    ready: pending.length ? <span className="badge warn">{pending.length} to confirm</span> : <span className="badge good">Reviewed</span>,
    'no-text': <span className="badge warn">No readable text</span>,
    unsupported: <span className="badge warn">Can&rsquo;t read this type</span>,
    error: <span className="badge bad">Could not read</span>,
  }[doc.status];

  const remove = async () => {
    if (!window.confirm(`Remove ${doc.name}? Figures confirmed from it will be removed too.`)) return;
    await deleteDocBlob(doc.id);
    update((w) => syncFinancialsFromDocuments({ ...w, documents: w.documents.filter((d) => d.id !== doc.id) }));
  };

  return (
    <article className="card doc-card">
      <div className="card-title">
        <div>
          <h3 style={{ wordBreak: 'break-word' }}>{doc.name}</h3>
          <div className="doc-meta">{DOC_KIND_LABELS[doc.kind]}{doc.pageCount ? ` · ${doc.pageCount} page${doc.pageCount === 1 ? '' : 's'}` : ''} · {(doc.size / 1024).toFixed(0)} KB</div>
        </div>
        {statusBadge}
      </div>
      {doc.statusDetail && <p className="callout small">{doc.statusDetail}</p>}
      {pending.map((c) => <CandidateRow key={c.id} doc={doc} c={c} />)}
      {done.length > 0 && (
        <details open={showDone} onToggle={(e) => setShowDone((e.target as HTMLDetailsElement).open)}>
          <summary className="small faint" style={{ cursor: 'pointer', marginTop: 8 }}>{done.length} reviewed figure{done.length === 1 ? '' : 's'}</summary>
          {done.map((c) => <CandidateRow key={c.id} doc={doc} c={c} />)}
        </details>
      )}
      <div className="row" style={{ marginTop: 12 }}>
        {doc.mime === 'application/pdf' && <button className="btn small ghost" type="button" onClick={() => void openSourcePage(getDocBlob, doc.id)}>Open</button>}
        <button className="btn small danger" type="button" onClick={() => void remove()}>Remove</button>
      </div>
    </article>
  );
}

function Conflicts() {
  const { ws, update } = useWorkspace();
  const conflicts = findConflicts(ws);
  if (conflicts.length === 0) return null;
  return (
    <section className="card" style={{ borderColor: 'var(--warn)' }}>
      <h2>Figures that differ between documents</h2>
      <p className="muted small">
        We don&rsquo;t pick one for you. Choose the figure that&rsquo;s right — and expect a lender to ask about the difference.
      </p>
      {conflicts.map((c) => (
        <fieldset key={c.field} style={{ border: 0, padding: 0, margin: '14px 0 0' }}>
          <legend><strong>{c.label}</strong>{c.period ? ` (${c.period})` : ''} — differ by {(c.spread * 100).toFixed(1)}%{c.resolved && <span className="badge good" style={{ marginLeft: 8 }}>Chosen</span>}</legend>
          <div className="conflict-figs">
            {c.figures.map((f) => (
              <label key={f.candidateId}>
                <input type="radio" name={`conf-${c.field}`} checked={c.chosenCandidateId === f.candidateId}
                  onChange={() => update((w) => resolveConflict(w, c.field, f.candidateId))} />
                <span>
                  <strong className="num">{fmtMoney(f.value)}</strong> from {f.docName}{f.page !== undefined ? `, page ${f.page}` : ''}
                  <span className="snip" style={{ display: 'block' }}>{f.snippet}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      ))}
    </section>
  );
}

const MANUAL_FIELDS: FinancialField[] = ['annualRevenue', 'operatingIncome', 'netIncome', 'existingAnnualDebtService', 'cash'];

function ManualFigures() {
  const { ws, update } = useWorkspace();
  const [drafts, setDrafts] = useState<Partial<Record<FinancialField, string>>>({});
  return (
    <section className="card">
      <h2>Your key figures</h2>
      <p className="muted small">
        Figures confirmed from documents appear here with their source. You can also enter one by hand — then confirm it
        when you&rsquo;re sure, so it can be used for the cash-flow check.
      </p>
      <div className="table-scroll">
        <table className="table">
          <thead><tr><th>Figure</th><th className="r">Amount</th><th>Source</th><th /></tr></thead>
          <tbody>
            {MANUAL_FIELDS.map((f) => {
              const fa = ws.profile[f];
              return (
                <tr key={f}>
                  <td>{FINANCIAL_FIELD_LABELS[f]}</td>
                  <td className="r">{fa ? fmtMoney(fa.value) : '—'}</td>
                  <td className="small">{fa ? describeSource(fa) : <span className="faint">Not set</span>}</td>
                  <td>
                    <form className="row" style={{ flexWrap: 'nowrap' }} onSubmit={(e) => {
                      e.preventDefault();
                      const p = parseAmount(drafts[f] ?? '');
                      if (!p) return;
                      update((w) => ({ ...w, profile: { ...w.profile, [f]: fact(p.value) } }));
                      setDrafts((d) => ({ ...d, [f]: '' }));
                    }}>
                      <input className="input" style={{ width: 120, minHeight: 34 }} aria-label={`Enter ${FINANCIAL_FIELD_LABELS[f]}`}
                        placeholder={f === 'existingAnnualDebtService' ? 'per year' : 'e.g. 85k'}
                        value={drafts[f] ?? ''} onChange={(e) => setDrafts((d) => ({ ...d, [f]: e.target.value }))} />
                      <button className="btn small" type="submit">Set</button>
                      {fa && fa.source === 'entered' && !fa.evidence && (
                        <button className="btn small primary" type="button" onClick={() => update((w) => confirmEnteredFigure(w, f))}>Confirm</button>
                      )}
                    </form>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function Documents() {
  const { ws } = useWorkspace();
  return (
    <>
      <PageHead eyebrow="Documents" title="Your documents and figures">
        Upload statements and returns. Figures are read on your device, shown with the page they came from, and only used
        once you confirm them.
      </PageHead>
      <div className="stack-lg">
        <Uploader />
        <Conflicts />
        {ws.documents.length > 0 && (
          <section className="stack">
            <h2>Uploaded</h2>
            {[...ws.documents].reverse().map((d) => <DocCard key={d.id} doc={d} />)}
          </section>
        )}
        <ManualFigures />
      </div>
    </>
  );
}
